import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader, Package, User, DollarSign } from 'lucide-react';
import { ordersApi } from '../api/client';
import { useTranslation } from '../i18n/useTranslation';

const OrderLookup = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [orderId, setOrderId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!orderId.trim()) return;

    setLoading(true);
    setError(null);
    setOrder(null);

    try {
      const response = await ordersApi.getOrder(orderId.trim());
      setOrder(response.data.data);
    } catch (err) {
      setError(err.response?.data?.error || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePaymentPlan = () => {
    if (order) {
      navigate(`/orders/${order.id}/payment`, { state: { order } });
    }
  };

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white">Buscar Pedido</h1>
        <p className="mt-2 text-sm text-gray-400">
          Digite o número do pedido para criar um plano de pagamento
        </p>
      </div>

      <div className="bg-dark-700 shadow rounded-lg p-6 border border-dark-600">
        <form onSubmit={handleSearch} className="space-y-4">
          <div>
            <label htmlFor="orderId" className="block text-sm font-medium text-gray-300 mb-2">
              Número do Pedido
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="orderId"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Digite o número do pedido"
                className="flex-1 px-4 py-2 border border-dark-500 bg-dark-600 text-white rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={loading || !orderId.trim()}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-dark-900 bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader className="h-5 w-5 animate-spin" />
                ) : (
                  <Search className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>
        </form>

        {error && (
          <div className="mt-4 bg-red-900 bg-opacity-50 border border-red-700 rounded-md p-4">
            <p className="text-sm text-red-200">{error}</p>
          </div>
        )}

        {order && (
          <div className="mt-6 border-t border-dark-600 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start">
                  <Package className="h-5 w-5 text-primary-500 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-white">Detalhes do Pedido</p>
                    <p className="text-sm text-gray-300">
                      Pedido #{order.number}
                    </p>
                    <p className="text-xs text-gray-400">ID: {order.id}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <User className="h-5 w-5 text-primary-500 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-white">Cliente</p>
                    <p className="text-sm text-gray-300">{order.customer.name}</p>
                    <p className="text-xs text-gray-400">{order.customer.email}</p>
                    {order.customer.phone && (
                      <p className="text-xs text-gray-400">{order.customer.phone}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start">
                  <DollarSign className="h-5 w-5 text-primary-500 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-white">Total</p>
                    <p className="text-2xl font-bold text-primary-500">
                      {order.currency} {order.total.toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-white mb-2">Produtos</p>
                <div className="space-y-2">
                  {order.products.map((product, index) => (
                    <div key={index} className="bg-dark-600 rounded-md p-3 border border-dark-500">
                      <p className="text-sm font-medium text-white">{product.name}</p>
                      <p className="text-xs text-gray-400">
                        Quantidade: {product.quantity} × {order.currency} {product.price ? product.price.toFixed(2) : '0.00'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={handleCreatePaymentPlan}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-dark-900 bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Criar Plano de Pagamento
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderLookup;
